import { useEffect } from 'react';

// Global keyboard shortcut hook. Each binding fires when the matching key
// is pressed AND the focus is NOT inside a text input / textarea / select
// (otherwise we'd hijack typing). Modifier keys (Ctrl/Cmd/Alt) reserved
// for the browser — shortcuts here are bare letters.
//
// Architecture spec § 21.4.1 lists hotkeys as a UX requirement for high-
// throughput operators. j/k = move down/up the alert feed. e/p/s = exec /
// pass / snooze the focused alert. ? = help.

export interface Shortcut {
  // Single character or special key ('?' / 'Escape' / 'Enter').
  key: string;
  // Description shown in the help dialog.
  description: string;
  handler: (e: KeyboardEvent) => void;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

export function useKeyboardShortcuts(shortcuts: Shortcut[]): void {
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      // Match key case-insensitively for letters.
      const pressed = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const match = shortcuts.find((s) => s.key.toLowerCase() === pressed);
      if (!match) return;
      e.preventDefault();
      match.handler(e);
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shortcuts]);
}
